// src/components/calculator/SettlementResults.jsx
// Minimal dependencies: React + Recharts
// npm i recharts

import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  Legend,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";

const C = {
  blue: "#4C6EF5",
  teal: "#12B886",
  amber: "#FAB005",
  gray: "#868E96",
  indigo: "#5C7CFA",
  red: "#FA5252",
  green: "#40C057",
  pink: "#F06595",
};

const COLORS = [
  C.blue,
  C.teal,
  C.pink,
  C.amber,
  C.indigo,
  C.green,
  C.red,
  C.gray,
];

// ---------- tiny helpers ----------
function dollars(n) {
  const x = Number(n ?? 0);
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}
function dollars2(n) {
  const x = Number(n ?? 0);
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}
function buildBalanceSeries({ initial, monthly, months }) {
  const arr = [];
  let bal = Math.max(0, Number(initial || 0));
  const m = Math.max(1, Number(months || 1));
  for (let i = 1; i <= m; i++) {
    bal = Math.max(0, bal - Number(monthly || 0));
    arr.push({ month: i, balance: Math.round(bal) });
  }
  return arr;
}

// ---------- component ----------
export default function SettlementResults({
  settlementCalculationResults,
  clearSettlementCalculation,
}) {
  // derive flat result directly from props
  const raw = useMemo(
    () =>
      Array.isArray(settlementCalculationResults)
        ? settlementCalculationResults?.[0]
        : settlementCalculationResults,
    [settlementCalculationResults]
  );
  const r = raw && typeof raw === "object" ? raw : {};

  // hasData heuristic (don’t rely on outcome being set)
  const hasData =
    Number(r.totalBalance || 0) > 0 ||
    Number(r.disposableIncome || r.monthlyCollectionAmount || 0) > 0 ||
    Number(r.planMonths || r.months || 0) > 0 ||
    (r.breakdown && Object.keys(r.breakdown).length > 0) ||
    (Array.isArray(r.problemYears) && r.problemYears.length > 0);

  // read fields safely
  const status = r.outcome ?? ""; // e.g., "6-Year Payment", "Offer In Compromise", "CNC"
  const totalBalance = Number(r.totalBalance || 0);
  const planMonths = Number(r.planMonths ?? r.months ?? 0);
  const monthly = Number(r.paymentAmount ?? r.monthlyPayment ?? 0);
  const di = Number(r.disposableIncome ?? r.monthlyCollectionAmount ?? 0);
  const incomeTotal = Number(r.totalIncome ?? 0);
  const expensesTotal = Number(r.totalExpenses ?? 0);
  const breakdown = r.breakdown || {};

  const offerBasis = Number(r.offerBasis ?? 0);
  const plausibleOffer = Number(r.offer ?? offerBasis);
  const downRequired = Number(
    r.downPayment ?? (offerBasis > 0 ? offerBasis * 0.2 : 0)
  );

  const sixYearTotal = Number(r.sixYearTotal ?? 0);
  const iaTotal = Number(r.installmentAgreementTotal ?? 0);
  const reasons = Array.isArray(r.reasons) ? r.reasons : [];
  const problemYears = Array.isArray(r.problemYears) ? r.problemYears : [];
  const expirations = Array.isArray(r.expirations) ? r.expirations : [];
  const dropSchedule = Array.isArray(r.dropSchedule) ? r.dropSchedule : [];
  const netAssets = Number(r.netAssets ?? 0);

  // derived bits
  const downResources = Math.max(0, di * 3 + netAssets);
  const downPct =
    downRequired > 0
      ? Math.min(100, Math.round((downResources / downRequired) * 100))
      : 0;
  const showOICGauge = status === "Offer In Compromise" && downRequired > 0;

  const piePrimary =
    status === "Offer In Compromise"
      ? [
          { name: "Plausible Offer", value: Math.max(0.01, plausibleOffer) },
          {
            name: "Remaining Liability",
            value: Math.max(0, totalBalance - plausibleOffer),
          },
        ]
      : [
          { name: "Payment", value: Math.max(0, monthly) },
          { name: "Remaining Disposable", value: Math.max(0, di - monthly) },
        ];

  const balanceSeries = useMemo(() => {
    const months = Math.max(
      1,
      status === "CNC" ? planMonths || 12 : planMonths || 1
    );
    const mth = status === "CNC" ? 0 : monthly;
    return buildBalanceSeries({ initial: totalBalance, monthly: mth, months });
  }, [status, totalBalance, monthly, planMonths]);

  const expenseList = useMemo(() => {
    const entries = Object.entries(breakdown || {}).map(([k, v]) => ({
      key: k,
      value: Number(v || 0),
    }));
    entries.sort((a, b) => b.value - a.value);
    return entries.slice(0, 5);
  }, [breakdown]);

  // sanity flags
  const checks = {
    diCoversPayment:
      monthly <= di || status === "CNC" || status === "Offer In Compromise",
    planMultiplies:
      planMonths > 0 ? Math.abs(monthly * planMonths - sixYearTotal) < 1 : true,
  };

  const onRecalc = () => {
    if (typeof clearSettlementCalculation === "function")
      clearSettlementCalculation();
  };

  // basic styles (inline to keep it simple)
  const s = {
    page: { background: "#f8f9fa", borderTop: "1px solid #e9ecef" },
    container: { maxWidth: 1100, margin: "0 auto", padding: "32px 16px" },
    headerRow: { marginBottom: 16 },
    badge: {
      marginLeft: 8,
      padding: "2px 8px",
      borderRadius: 6,
      fontSize: 13,
      background:
        status === "Offer In Compromise"
          ? "#e5dbff"
          : status === "DDIA" || status === "6-Year Payment"
          ? "#e7f5ff"
          : status === "PPIA"
          ? "#fff4e6"
          : status === "CNC"
          ? "#ebfbee"
          : "#ffe3e3",
      color: "#343a40",
      border: "1px solid #dee2e6",
    },
    kpis: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
      gap: 12,
      marginBottom: 16,
    },
    tile: {
      border: "1px solid #e9ecef",
      borderRadius: 8,
      padding: 12,
      background: "#fff",
    },
    tileLabel: { fontSize: 13, color: "#666" },
    tileValue: { fontSize: 22, fontWeight: 700 },
    tileHint: { fontSize: 12, color: "#888", marginTop: 4 },
    grid3: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: 16,
    },
    card: {
      border: "1px solid #e9ecef",
      borderRadius: 8,
      padding: 12,
      background: "#fff",
      minHeight: 340,
    },
    cardTitle: { fontSize: 14, fontWeight: 700, marginBottom: 8 },
    sub: { color: "#666", fontSize: 14 },
    details3: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: 16,
    },
    listRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "6px 0",
      borderBottom: "1px dashed #f1f3f5",
    },
    tags: { display: "flex", gap: 8, flexWrap: "wrap" },
    tag: {
      background: "#f1f3f5",
      border: "1px solid #e9ecef",
      borderRadius: 999,
      padding: "2px 8px",
      fontSize: 12,
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
    th: {
      textAlign: "left",
      borderBottom: "1px solid #eee",
      padding: "8px 6px",
    },
    td: { borderBottom: "1px solid #f1f3f5", padding: "8px 6px" },
    buttons: {
      display: "flex",
      gap: 8,
      justifyContent: "flex-end",
      marginTop: 16,
    },
    button: {
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid #dee2e6",
      background: "#fff",
      cursor: "pointer",
    },
    buttonPrimary: {
      padding: "10px 14px",
      borderRadius: 8,
      border: "1px solid #4c6ef5",
      background: "#4c6ef5",
      color: "#fff",
      cursor: "pointer",
    },
    hr: { border: 0, borderTop: "1px solid #e9ecef", margin: "24px 0" },
  };

  if (!hasData) {
    return (
      <div style={s.page}>
        <div style={s.container}>
          <div style={s.card}>
            <div style={s.cardTitle}>No results yet</div>
            <div className="sub">Please complete the calculator first.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={s.headerRow}>
          <h2 style={{ display: "inline-block", margin: 0 }}>
            Your Estimated Outcome
          </h2>
          <span style={s.badge}>{status || "—"}</span>
          <p style={{ ...s.sub, marginTop: 8, maxWidth: 720 }}>
            This is an educational estimate based on the information you
            entered. Actual options depend on full documentation and IRS/state
            review.
          </p>
        </div>

        {/* KPIs */}
        <div style={s.kpis}>
          <div style={s.tile}>
            <div style={s.tileLabel}>Total Balance</div>
            <div style={s.tileValue}>{dollars(totalBalance)}</div>
            <div style={s.tileHint}>Estimated IRS balance due</div>
          </div>
          <div style={s.tile}>
            <div style={s.tileLabel}>Disposable Income (mo.)</div>
            <div style={s.tileValue}>{dollars2(di)}</div>
            <div style={s.tileHint}>
              {checks.diCoversPayment
                ? "After standard allowances"
                : "⚠ Payment exceeds DI"}
            </div>
          </div>
          <div style={s.tile}>
            <div style={s.tileLabel}>Plan</div>
            <div style={s.tileValue}>
              {status === "CNC"
                ? "No monthly payment"
                : `${Math.max(0, planMonths)} mo @ ${dollars2(monthly)}`}
            </div>
            <div style={s.tileHint}>
              {status === "Offer In Compromise"
                ? "Offer timeline varies"
                : "Illustrative payoff path"}
            </div>
          </div>
        </div>

        {/* Charts row */}
        <div style={s.grid3}>
          {/* Pie 1 */}
          <div style={s.card}>
            <div style={s.cardTitle}>
              {status === "Offer In Compromise"
                ? "Offer vs Liability"
                : "Payment vs Disposable Income"}
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={piePrimary}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    {piePrimary.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip formatter={(v) => dollars2(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* OIC Gauge or Income vs Allowable */}
          <div style={s.card}>
            <div style={s.cardTitle}>
              {status === "Offer In Compromise"
                ? "Down Payment Readiness"
                : "Income vs Allowable"}
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                {showOICGauge ? (
                  <RadialBarChart
                    innerRadius="60%"
                    outerRadius="100%"
                    data={[{ name: "Progress", value: downPct }]}
                    startAngle={180}
                    endAngle={0}
                  >
                    <PolarAngleAxis
                      type="number"
                      domain={[0, 100]}
                      tick={false}
                    />
                    <RadialBar dataKey="value" cornerRadius={8} fill={C.teal} />
                    <RTooltip formatter={(v) => `${v}%`} />
                  </RadialBarChart>
                ) : (
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Income", value: Math.max(0, incomeTotal) },
                        {
                          name: "Allowable Expenses",
                          value: Math.max(0, expensesTotal),
                        },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                    >
                      <Cell fill={C.blue} />
                      <Cell fill={C.amber} />
                    </Pie>
                    <RTooltip formatter={(v) => dollars2(v)} />
                    <Legend />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
            {status === "Offer In Compromise" && (
              <div style={{ ...s.sub, marginTop: 8 }}>
                Resources: {dollars2(downResources)} • Required down:{" "}
                {dollars2(downRequired)} • Progress: {downPct}%
              </div>
            )}
          </div>

          {/* Balance over time */}
          <div style={s.card}>
            <div style={s.cardTitle}>Balance Over Time</div>
            <div style={{ ...s.sub, marginBottom: 4 }}>
              {status === "CNC"
                ? "No payment required"
                : `${Math.max(0, planMonths)} months @ ${dollars2(monthly)}`}
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={balanceSeries}
                  margin={{ left: 0, right: 6, top: 10, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={C.indigo}
                        stopOpacity={0.5}
                      />
                      <stop offset="95%" stopColor={C.indigo} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RTooltip formatter={(v) => dollars2(v)} />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke={C.indigo}
                    fill="url(#g1)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <hr style={s.hr} />

        {/* Details row */}
        <div style={s.details3}>
          {/* Expenses */}
          <div style={s.card}>
            <div style={s.cardTitle}>Top Expenses</div>
            {expenseList.length === 0 ? (
              <div className="sub" style={s.sub}>
                No expenses provided.
              </div>
            ) : (
              expenseList.map((e) => (
                <div key={e.key} style={s.listRow}>
                  <span style={{ textTransform: "capitalize" }}>{e.key}</span>
                  <strong>{dollars2(e.value)}</strong>
                </div>
              ))
            )}
          </div>

          {/* Drop Schedule */}
          <div style={s.card}>
            <div style={s.cardTitle}>Drop Schedule</div>
            {dropSchedule.length === 0 ? (
              <div className="sub" style={s.sub}>
                No scheduled drops.
              </div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Month</th>
                    <th style={{ ...s.th, textAlign: "right" }}>Payment</th>
                    <th style={s.th}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {dropSchedule.map((d, idx) => (
                    <tr key={idx}>
                      <td style={s.td}>
                        {d.year + 10 ?? d.atMonth ?? idx + 1}
                      </td>
                      <td style={{ ...s.td, textAlign: "right" }}>
                        {dollars2(d.amount * 0.25 ?? d.payment * 0.25 ?? 0)}
                      </td>
                      <td style={s.td}>{d.notes ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Years & Reasons */}
          <div style={s.card}>
            <div style={s.cardTitle}>Years & Reasons</div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Problem Years
              </div>
              <div style={s.tags}>
                {problemYears.length ? (
                  problemYears.map((y) => (
                    <span key={y} style={s.tag}>
                      {y}
                    </span>
                  ))
                ) : (
                  <span style={s.sub}>—</span>
                )}
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Expiration Years
              </div>
              <div style={s.tags}>
                {expirations.length ? (
                  expirations.map((y) => (
                    <span key={y} style={s.tag}>
                      {y}
                    </span>
                  ))
                ) : (
                  <span style={s.sub}>—</span>
                )}
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Reasons</div>
              <div>
                {reasons.length ? (
                  reasons.map((r, i) => <div key={i}>• {r}</div>)
                ) : (
                  <span style={s.sub}>—</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <hr style={s.hr} />

        {/* What this means */}
        <div style={s.card}>
          <div style={s.cardTitle}>What this means</div>
          {status === "Offer In Compromise" ? (
            <>
              <p>
                You may qualify to settle for less than the full amount based on
                your estimated ability to pay over the remaining collection
                window.
              </p>
              <p>
                A typical Offer requires a down payment and strict compliance
                until acceptance and completion.
              </p>
            </>
          ) : status === "CNC" ? (
            <p>
              Currently Not Collectible indicates no monthly payment at this
              time. Staying compliant is essential until the collection window
              expires.
            </p>
          ) : status === "Full Pay" ? (
            <p>
              Your income/assets indicate full payment may be required. The
              12-month illustration is one possible path; other timelines may
              fit your documentation.
            </p>
          ) : (
            <p>
              A payment plan looks likely based on your disposable income and
              time remaining. The chart shows an illustrative amortization using
              the monthly amount above.
            </p>
          )}
        </div>

        {/* Footer buttons */}
        <div style={s.buttons}>
          <button type="button" style={s.button} onClick={onRecalc}>
            Recalculate
          </button>
          <a href={`/tax-relief-companies`} style={s.buttonPrimary}>
            Get Help with My Case
          </a>
        </div>
      </div>
    </div>
  );
}
